/* assets/mr-giftcard-guard.js
   Maison Roziere — Gift Card Standalone Guard (Luxury Modal + Deterministic Rebuild)

   Goal:
   - Gift cards must be purchased separately from physical items.
   - If a mixed cart occurs, show a premium modal with choices:
       1) Continue with Gift Card  -> keep ONLY gift card lines (including the newly-added one)
       2) Continue with Items      -> keep ONLY physical lines (including the newly-added one)
       3) Cancel                  -> revert cart EXACTLY to the state BEFORE the add (remove newly-added item)

   This version:
   - ✅ Keeps loader INSIDE the modal (visible while work happens)
   - ✅ Removes the refining toast AND removes ALL post-action toasts from modal buttons
   - ✅ Adds a safe resolution event for wishlist “decision-delay” flows
   - ✅ Overlay click + Escape behave as "Cancel"
   - ✅ Prevents modal click-handler stacking across multiple opens
   - ✅ Persisted, luxury page-loader overlay survives window reload (reduces flicker)
   - ✅ Modal loader copy adapts to choice (prep vs restore)
   - ✅ Page loader copy adapts to choice across reload (refine vs restore)
   - ✅ FIX (IMPORTANT): Page loader text is set IMMEDIATELY before showing (no “confused” carryover)
   - Keeps everything else unchanged and intact
*/

(function () {
  if (window.__MR_GiftCardGuardModal_Bound) return;
  window.__MR_GiftCardGuardModal_Bound = true;

  // -----------------------------
  // Config
  // -----------------------------
  var MODAL_ID = "mr-gc-standalone-modal";
  var TOAST_DURATION_MS = 9000; // kept intact (not used by modal buttons anymore)

  // -----------------------------
  // Persisted Page Loader (survives reload)
  // -----------------------------
  var RELOAD_FLAG_KEY = "__MR_GC_GUARD_RELOAD_PENDING__";
  var RELOAD_TEXT_KEY = "__MR_GC_GUARD_RELOAD_TEXT__";
  var PAGE_LOADER_ID = "mr-gc-page-loader";
  var PAGE_LOADER_TEXT_ID = "mr-gc-page-loader-text";

  function ensurePageLoaderStyles() {
    if (document.getElementById("mr-gc-page-loader-styles")) return;

    var css = document.createElement("style");
    css.id = "mr-gc-page-loader-styles";
    css.textContent = `
      /* Maison Roziere — Persisted Page Loader */
      #${PAGE_LOADER_ID}{
        position: fixed;
        inset: 0;
        z-index: 100002;
        display: none;
      }
      #${PAGE_LOADER_ID}.is-visible{ display:block; }

      #${PAGE_LOADER_ID} .mr-pl__overlay{
        position:absolute;
        inset:0;
        background: rgba(0,0,0,0.62);
        backdrop-filter: blur(3px);
      }

      #${PAGE_LOADER_ID} .mr-pl__panel{
        position:absolute;
        top:50%;
        left:50%;
        transform: translate(-50%,-50%);
        width: min(520px, calc(100vw - 2rem));
        background: rgb(var(--background));
        border: 1px solid rgba(0,0,0,0.10);
        border-radius: 18px;
        box-shadow: 0 18px 60px rgba(0,0,0,0.40);
        padding: 1.15rem 1.25rem;
        text-align: center;
      }

      #${PAGE_LOADER_ID} .mr-pl__label{
        font-size: 0.72rem;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        opacity: 0.78;
        margin-bottom: 0.65rem;
      }

      #${PAGE_LOADER_ID} .mr-pl__row{
        display:flex;
        align-items:center;
        justify-content:center;
        gap: 0.6rem;
      }

      #${PAGE_LOADER_ID} .mr-pl__spinner{
        width: 18px;
        height: 18px;
        border-radius: 999px;
        border: 2px solid rgba(0,0,0,0.18);
        border-top-color: rgba(0,0,0,0.60);
        animation: mrplspin .8s linear infinite;
      }

      #${PAGE_LOADER_ID} .mr-pl__text{
        font-size: 0.92rem;
        line-height: 1.5;
        opacity: 0.88;
      }

      @keyframes mrplspin { to { transform: rotate(360deg); } }
    `;
    document.head.appendChild(css);
  }

  function setNextReloadLoaderText(text) {
    try { sessionStorage.setItem(RELOAD_TEXT_KEY, String(text || "")); } catch (e) {}
  }

  function getNextReloadLoaderText() {
    try { return sessionStorage.getItem(RELOAD_TEXT_KEY) || ""; } catch (e) {}
    return "";
  }

  function clearNextReloadLoaderText() {
    try { sessionStorage.removeItem(RELOAD_TEXT_KEY); } catch (e) {}
  }

  function ensurePageLoader() {
    ensurePageLoaderStyles();
    var el = document.getElementById(PAGE_LOADER_ID);
    if (el) return el;

    el = document.createElement("div");
    el.id = PAGE_LOADER_ID;
    el.setAttribute("aria-hidden", "true");
    el.innerHTML =
      '<div class="mr-pl__overlay"></div>' +
      '<div class="mr-pl__panel" role="status" aria-live="polite">' +
        '<div class="mr-pl__label">Client Services Note</div>' +
        '<div class="mr-pl__row">' +
          '<div class="mr-pl__spinner" aria-hidden="true"></div>' +
          '<div class="mr-pl__text" id="' + PAGE_LOADER_TEXT_ID + '">Refining your cart…</div>' +
        "</div>" +
      "</div>";

    document.body.appendChild(el);
    return el;
  }

  // ✅ FIX: Always set overlay text immediately (DOM) before showing
  function setPageLoaderTextNow(text) {
    try {
      var t = document.getElementById(PAGE_LOADER_TEXT_ID);
      if (t) t.textContent = String(text || "");
    } catch (e) {}
  }

  // ✅ FIX: show overlay + persist BOTH the flag and the text for the next load
  function showPageLoaderPersisted(overlayText) {
    var txt = (overlayText != null && overlayText !== "") ? String(overlayText) : "";

    // Ensure loader exists so we can update text before showing
    ensurePageLoader();

    if (txt) {
      setPageLoaderTextNow(txt);
      setNextReloadLoaderText(txt);
    } else {
      // Prevent stale carryover on the next page load
      clearNextReloadLoaderText();
    }

    try { sessionStorage.setItem(RELOAD_FLAG_KEY, "1"); } catch (e) {}

    var el = document.getElementById(PAGE_LOADER_ID);
    if (!el) return;
    el.classList.add("is-visible");
    el.setAttribute("aria-hidden", "false");
    try { document.documentElement.style.overflow = "hidden"; } catch (e) {}
  }

  function showPageLoaderIfFlagged() {
    var flagged = false;
    try { flagged = sessionStorage.getItem(RELOAD_FLAG_KEY) === "1"; } catch (e) {}
    if (!flagged) return;

    var el = ensurePageLoader();
    el.classList.add("is-visible");
    el.setAttribute("aria-hidden", "false");
    try { document.documentElement.style.overflow = "hidden"; } catch (e) {}

    // Apply one-time loader text (if provided)
    try {
      var txt = getNextReloadLoaderText();
      if (txt) setPageLoaderTextNow(txt);
    } catch (e) {}
    clearNextReloadLoaderText();

    // Clear flag so it won't persist
    try { sessionStorage.removeItem(RELOAD_FLAG_KEY); } catch (e) {}

    // Hide shortly after load
    window.addEventListener("load", function () {
      setTimeout(function () {
        try {
          el.classList.remove("is-visible");
          el.setAttribute("aria-hidden", "true");
          document.documentElement.style.overflow = "";
        } catch (e) {}
      }, 220);
    }, { once: true });
  }

  // Init persisted loader on every page (no-op unless flagged)
  try {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", showPageLoaderIfFlagged, { once: true });
    } else {
      showPageLoaderIfFlagged();
    }
  } catch (e) {}

  // -----------------------------
  // Luxe Toast (kept intact; no longer used for modal button actions)
  // -----------------------------
  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function houseToast(label, msg, duration) {
    duration = duration || TOAST_DURATION_MS;

    var existing = document.getElementById("mr-house-toast");
    if (existing) existing.remove();

    var el = document.createElement("div");
    el.id = "mr-house-toast";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.innerHTML =
      '<div class="mr-house-toast__inner">' +
        '<div class="mr-house-toast__label">' + escapeHtml(label) + '</div>' +
        '<div class="mr-house-toast__msg">' + escapeHtml(msg) + "</div>" +
      "</div>";

    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add("is-visible"); });

    setTimeout(function () {
      el.classList.remove("is-visible");
      setTimeout(function () { el.remove(); }, 250);
    }, duration);
  }

  function showToastLux(opts) {
    var label = (opts && opts.label) ? opts.label : "Client Services Note";
    var title = (opts && opts.title) ? opts.title : "";
    var duration = (opts && opts.duration) ? opts.duration : TOAST_DURATION_MS;

    if (typeof window.showWishlistToast === "function") {
      window.showWishlistToast({
        label: label,
        title: title,
        image: (opts && opts.image) ? opts.image : "",
        linkHref: (opts && opts.linkHref) ? opts.linkHref : (Shopify.routes.root + "cart"),
        linkText: (opts && opts.linkText) ? opts.linkText : "Review cart",
        duration: duration,
        timeout: duration,
        delay: duration
      });
      return;
    }

    houseToast(label, title, duration);
  }

  // -----------------------------
  // Cart Utilities
  // -----------------------------
  async function fetchCart() {
    var res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to load cart");
    return res.json();
  }

  async function clearCart() {
    await fetch("/cart/clear.js", { method: "POST", credentials: "same-origin" });
  }

  function normalizeCartItems(items) {
    return (items || []).map(function (i) {
      return {
        variant_id: i.variant_id,
        quantity: i.quantity || 1,
        properties: i.properties || null,
        gift_card: !!i.gift_card
      };
    });
  }

  function cleanProperties(props) {
    if (!props || typeof props !== "object") return null;
    var out = {};
    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      if (props[k] == null) continue;
      out[k] = String(props[k]);
    }
    return Object.keys(out).length ? out : null;
  }

  async function addItems(items) {
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var payload = {
        id: Number(it.variant_id),
        quantity: Number(it.quantity || 1)
      };

      var props = cleanProperties(it.properties);
      if (props) payload.properties = props;

      var res = await fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Failed to rebuild cart.");
    }
  }

  // ✅ Accept an overlay text so it never uses stale text
  function refreshCartUI(overlayText) {
    try { window.dispatchEvent(new CustomEvent("cart:refresh")); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent("cart:updated")); } catch (e) {}

    setTimeout(function () {
      // ✅ Persisted overlay + correct text set immediately
      showPageLoaderPersisted(overlayText || "Refining your cart…");

      // Allow paint before reload starts
      setTimeout(function () {
        window.location.reload();
      }, 60);
    }, 350);
  }

  // -----------------------------
  // Request Identification
  // -----------------------------
  function sameOrigin(url) {
    try {
      var u = new URL(url, window.location.href);
      return u.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  function isCartAdd(url) {
    if (!url) return false;
    try {
      var u = new URL(url, window.location.href);
      return u.pathname === "/cart/add" || u.pathname === "/cart/add.js";
    } catch (e) {
      return String(url).indexOf("/cart/add") !== -1;
    }
  }

  // -----------------------------
  // Modal UI
  // -----------------------------
  function ensureModalStyles() {
    if (document.getElementById("mr-gc-modal-styles")) return;

    var css = document.createElement("style");
    css.id = "mr-gc-modal-styles";
    css.textContent = `
      /* Maison Roziere — Gift Card Standalone Modal */
      #${MODAL_ID} {
        position: fixed;
        inset: 0;
        z-index: 100000;
        display: none;
      }
      #${MODAL_ID}.is-open { display: block; }

      #${MODAL_ID} .mr-modal__overlay {
        position: absolute;
        inset: 0;
        background: rgba(0,0,0,0.58);
        backdrop-filter: blur(2px);
      }

      #${MODAL_ID} .mr-modal__panel {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(560px, calc(100vw - 2rem));
        background: rgb(var(--background));
        border: 1px solid rgba(0,0,0,0.10);
        border-radius: 18px;
        box-shadow: 0 18px 60px rgba(0,0,0,0.40);
        padding: 1.35rem 1.35rem 1.1rem;
      }

      #${MODAL_ID} .mr-modal__label {
        font-size: 0.74rem;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        opacity: 0.78;
        margin-bottom: 0.55rem;
      }

      #${MODAL_ID} .mr-modal__title {
        font-size: 1.08rem;
        line-height: 1.35;
        margin: 0 0 0.65rem;
      }

      #${MODAL_ID} .mr-modal__body {
        font-size: 0.95rem;
        line-height: 1.55;
        opacity: 0.86;
        margin: 0 0 1rem;
      }

      #${MODAL_ID} .mr-modal__actions {
        display: grid;
        gap: 0.65rem;
        margin-top: 0.25rem;
      }

      #${MODAL_ID} .mr-modal__btn {
        width: 100%;
        padding: 0.9rem 1rem;
        border-radius: 999px;
        border: 1px solid rgba(0,0,0,0.16);
        background: transparent;
        cursor: pointer;
        font: inherit;
        transition: transform .08s ease, opacity .15s ease;
      }

      #${MODAL_ID} .mr-modal__btn:active { transform: scale(0.99); }

      #${MODAL_ID} .mr-modal__btn--primary {
        background: rgb(var(--text-color));
        color: rgb(var(--background));
        border-color: rgb(var(--text-color));
      }

      #${MODAL_ID} .mr-modal__btn--subtle {
        opacity: 0.92;
      }

      /* Loading state */
      #${MODAL_ID}.is-busy .mr-modal__btn {
        pointer-events: none;
        opacity: 0.55;
      }
      #${MODAL_ID} .mr-modal__loader {
        display: none;
        margin-top: 0.9rem;
        font-size: 0.85rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        opacity: 0.75;
      }
      #${MODAL_ID}.is-busy .mr-modal__loader { display: block; }

      /* House toast fallback */
      #mr-house-toast {
        position: fixed;
        left: 50%;
        bottom: 18px;
        transform: translateX(-50%) translateY(10px);
        opacity: 0;
        z-index: 100001;
        transition: opacity .2s ease, transform .2s ease;
        width: min(560px, calc(100vw - 2rem));
        pointer-events: none;
      }
      #mr-house-toast.is-visible {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
      .mr-house-toast__inner {
        background: rgba(18,18,18,0.92);
        color: #fff;
        border-radius: 16px;
        padding: 0.95rem 1.05rem;
        box-shadow: 0 14px 44px rgba(0,0,0,0.38);
      }
      .mr-house-toast__label {
        font-size: 0.72rem;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        opacity: 0.92;
        margin-bottom: 0.25rem;
      }
      .mr-house-toast__msg {
        font-size: 0.95rem;
        line-height: 1.35;
        opacity: 0.96;
      }
    `;
    document.head.appendChild(css);
  }

  function ensureModal() {
    ensureModalStyles();
    var el = document.getElementById(MODAL_ID);
    if (el) return el;

    el = document.createElement("div");
    el.id = MODAL_ID;
    el.setAttribute("aria-hidden", "true");

    el.innerHTML =
      '<div class="mr-modal__overlay" data-mr-modal-close></div>' +
      '<div class="mr-modal__panel" role="dialog" aria-modal="true" aria-labelledby="mr-gc-modal-title">' +
        '<div class="mr-modal__label">Client Services Note</div>' +
        '<h3 class="mr-modal__title" id="mr-gc-modal-title">Gift cards are processed separately</h3>' +
        '<p class="mr-modal__body">Gift cards must be purchased in a separate checkout from physical items. Please choose how you’d like to proceed.</p>' +
        '<div class="mr-modal__actions">' +
          '<button class="mr-modal__btn mr-modal__btn--primary" data-mr-choice="gift">Proceed with Gift Card</button>' +
          '<button class="mr-modal__btn mr-modal__btn--subtle" data-mr-choice="physical">Proceed with Items</button>' +
          '<button class="mr-modal__btn" data-mr-choice="cancel">Cancel</button>' +
        "</div>" +
        '<div class="mr-modal__loader"><span id="mr-gc-modal-loader-text">Preparing your selection…</span></div>' +
      "</div>";

    document.body.appendChild(el);
    return el;
  }

  function openModal() {
    var el = ensureModal();
    el.classList.add("is-open");
    el.classList.remove("is-busy");
    el.setAttribute("aria-hidden", "false");
    var btn = el.querySelector('[data-mr-choice="gift"]');
    if (btn) btn.focus();
    return el;
  }

  function setModalBusy(isBusy) {
    var el = document.getElementById(MODAL_ID);
    if (!el) return;
    if (isBusy) el.classList.add("is-busy");
    else el.classList.remove("is-busy");
  }

  function setModalLoaderText(text) {
    try {
      var el = document.getElementById("mr-gc-modal-loader-text");
      if (!el) return;
      el.textContent = String(text || "");
    } catch (e) {}
  }

  function loaderCopyForChoice(choice) {
    if (choice === "cancel") return "Restoring your cart…";
    return "Preparing a separate checkout…";
  }

  function closeModal() {
    var el = document.getElementById(MODAL_ID);
    if (!el) return;
    el.classList.remove("is-open");
    el.classList.remove("is-busy");
    el.setAttribute("aria-hidden", "true");
  }

  // -----------------------------
  // Deterministic Resolution
  // -----------------------------
  async function resolveWithChoice(choice, context) {
    if (!context || !Array.isArray(context.beforeItems) || !context.addedItem) {
      throw new Error("Missing modal context.");
    }

    var combined = context.beforeItems.concat([context.addedItem]);

    var keep = [];
    if (choice === "gift") {
      keep = combined.filter(function (i) { return !!i.gift_card; });
    } else if (choice === "physical") {
      keep = combined.filter(function (i) { return !i.gift_card; });
    } else if (choice === "cancel") {
      keep = context.beforeItems;
    } else {
      return;
    }

    var addedVariantId = context.addedItem && context.addedItem.variant_id ? context.addedItem.variant_id : null;
    var keptAdded = !!keep.some(function(i){ return String(i.variant_id) === String(addedVariantId); });

    await clearCart();
    if (keep.length) await addItems(keep);

    try {
      window.dispatchEvent(new CustomEvent("mr:giftcard-guard:resolved", {
        detail: {
          choice: choice,
          addedVariantId: addedVariantId,
          keptAdded: keptAdded
        }
      }));
    } catch (e) {}

    // ✅ Choose overlay text deterministically
    var overlayText = (choice === "cancel") ? "Restoring your cart…" : "Refining your cart…";

    // ✅ Reload with the correct loader text (now + across reload)
    refreshCartUI(overlayText);
  }

  // -----------------------------
  // Intercept /cart/add
  // -----------------------------
  var resolving = false;
  var activeContext = null;
  var actionInFlight = false;

  async function cancelActiveContext() {
    try {
      var el = document.getElementById(MODAL_ID);
      if (!el || !el.classList.contains("is-open")) return;
      if (!activeContext) return;

      if (actionInFlight) return;
      actionInFlight = true;

      setModalLoaderText(loaderCopyForChoice("cancel"));
      setModalBusy(true);

      await resolveWithChoice("cancel", activeContext);
    } catch (e) {
      showToastLux({
        label: "Client Services Note",
        title: "We couldn’t refine the cart automatically. Please refresh and try again.",
        duration: TOAST_DURATION_MS
      });

      // ✅ Avoid stale message on error fallback
      refreshCartUI("Refining your cart…");
    } finally {
      setModalBusy(false);
      closeModal();
      activeContext = null;
      actionInFlight = false;
      resolving = false;
    }
  }

  var nativeFetch = window.fetch;

  window.fetch = function (input, init) {
    try {
      var url = (typeof input === "string") ? input : (input && input.url ? input.url : "");
      init = init || {};
      var method = (init.method || "GET").toUpperCase();

      if (!isCartAdd(url) || method !== "POST" || !sameOrigin(url)) {
        return nativeFetch(input, init);
      }

      return (async function () {
        if (resolving) return nativeFetch(input, init);

        var before = await fetchCart().catch(function () { return null; });
        var beforeItems = normalizeCartItems(before && before.items ? before.items : []);
        var beforeHasGift = beforeItems.some(function (i) { return !!i.gift_card; });
        var beforeHasPhysical = beforeItems.some(function (i) { return !i.gift_card; });

        var res = await nativeFetch(input, init);
        if (!res.ok) return res;

        var added = null;
        try { added = await res.clone().json(); } catch (e) {}

        if (!added || !added.variant_id) return res;

        var addedItem = {
          variant_id: added.variant_id,
          quantity: added.quantity || 1,
          properties: added.properties || null,
          gift_card: !!added.gift_card
        };

        var createdMix =
          (beforeHasGift && !addedItem.gift_card) ||
          (beforeHasPhysical && addedItem.gift_card);

        if (!createdMix) return res;

        resolving = true;

        var context = {
          beforeItems: beforeItems,
          addedItem: addedItem
        };

        activeContext = context;

        var modal = openModal();

        function onClick(e) {
          var t = e.target.closest("[data-mr-choice]");
          if (!t) return;

          var choice = t.getAttribute("data-mr-choice");

          setModalLoaderText(loaderCopyForChoice(choice));
          actionInFlight = true;
          setModalBusy(true);

          resolveWithChoice(choice, context)
            .catch(function () {
              showToastLux({
                label: "Client Services Note",
                title: "We couldn’t refine the cart automatically. Please refresh and try again.",
                duration: TOAST_DURATION_MS
              });
              refreshCartUI("Refining your cart…");
            })
            .finally(function () {
              actionInFlight = false;
              setModalBusy(false);
              closeModal();
              resolving = false;
              activeContext = null;
            });
        }

        if (modal.__mrOnClick) {
          try { modal.removeEventListener("click", modal.__mrOnClick); } catch (e) {}
        }
        modal.addEventListener("click", onClick, { once: false });
        modal.__mrOnClick = onClick;

        return res;
      })();
    } catch (e) {
      return nativeFetch(input, init);
    }
  };

  // Overlay click now behaves as Cancel (revert to BEFORE add)
  document.addEventListener("click", function (e) {
    var overlay = e.target && e.target.closest && e.target.closest("[data-mr-modal-close]");
    if (!overlay) return;

    var el = document.getElementById(MODAL_ID);
    if (el && el.classList.contains("is-open")) {
      cancelActiveContext();
    }
  });

  // Escape now behaves as Cancel (revert to BEFORE add)
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    var el = document.getElementById(MODAL_ID);
    if (el && el.classList.contains("is-open")) {
      cancelActiveContext();
    }
  });

})();
